import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type { ODataResponse } from '@/lib/models/common';
import type {
	CrossCompanyMirrorConfig,
	CrossCompanyWorker,
	CrossCompanyJobOrder,
	CrossCompanyJobActivity,
} from '@/lib/models/crossCompanyMirrorConfig';

/**
 * Interfaccia per gli endpoints CrossCompanyMirrorConfig
 */
export interface CrossCompanyMirrorConfigEndpoints {
	/**
	 * Recupera lista paginata di configurazioni cross-company con query OData
	 * @param query - Query string OData
	 * @returns Promise con FetchResponse contenente ODataResponse<CrossCompanyMirrorConfig>
	 */
	getCrossCompanyMirrorConfigs(
		query: string,
	): Promise<FetchResponse<ODataResponse<CrossCompanyMirrorConfig>>>;

	/**
	 * Recupera una singola configurazione cross-company per ID
	 * @param id - ID della configurazione
	 * @returns Promise con FetchResponse contenente CrossCompanyMirrorConfig
	 */
	getCrossCompanyMirrorConfig(id: number): Promise<FetchResponse<CrossCompanyMirrorConfig>>;

	/**
	 * Crea una nuova configurazione cross-company
	 * @param payload - Dati della configurazione
	 * @returns Promise con FetchResponse contenente CrossCompanyMirrorConfig creata
	 */
	postCrossCompanyMirrorConfig(
		payload: Partial<CrossCompanyMirrorConfig>,
	): Promise<FetchResponse<CrossCompanyMirrorConfig>>;

	/**
	 * Aggiorna una configurazione cross-company esistente
	 * @param id - ID della configurazione
	 * @param payload - Dati da aggiornare
	 * @returns Promise con FetchResponse contenente CrossCompanyMirrorConfig aggiornata
	 */
	updateCrossCompanyMirrorConfig(
		id: number,
		payload: Partial<CrossCompanyMirrorConfig>,
	): Promise<FetchResponse<CrossCompanyMirrorConfig>>;

	/**
	 * Elimina una configurazione cross-company
	 * @param id - ID della configurazione da eliminare
	 * @returns Promise con FetchResponse contenente void
	 */
	deleteCrossCompanyMirrorConfig(id: number): Promise<FetchResponse<void>>;

	/**
	 * Recupera lista delle company target disponibili
	 * @returns Promise con FetchResponse contenente array di company target
	 */
	getTargetCompanies(): Promise<FetchResponse<string[]>>;

	/**
	 * Recupera workers della company target con query
	 * @param targetCompany - Company target
	 * @param query - Query string
	 * @returns Promise con FetchResponse contenente array di CrossCompanyWorker
	 */
	getCrossCompanyWorkers(
		targetCompany: string,
		query: string,
	): Promise<FetchResponse<CrossCompanyWorker[]>>;

	/**
	 * Recupera job orders della company target con query
	 * @param targetCompany - Company target
	 * @param query - Query string
	 * @returns Promise con FetchResponse contenente array di CrossCompanyJobOrder
	 */
	getCrossCompanyJobOrders(
		targetCompany: string,
		query: string,
	): Promise<FetchResponse<CrossCompanyJobOrder[]>>;

	/**
	 * Recupera job activities della company target con query
	 * @param targetCompany - Company target
	 * @param query - Query string
	 * @returns Promise con FetchResponse contenente array di CrossCompanyJobActivity
	 */
	getCrossCompanyJobActivities(
		targetCompany: string,
		query: string,
	): Promise<FetchResponse<CrossCompanyJobActivity[]>>;
}

/**
 * Implementazione degli endpoints per CrossCompanyMirrorConfig
 * Mantiene compatibilità con il JavaScript originale
 */
const crossCompanyMirrorConfigEndpoints: CrossCompanyMirrorConfigEndpoints = {
	getCrossCompanyMirrorConfigs(
		query: string,
	): Promise<FetchResponse<ODataResponse<CrossCompanyMirrorConfig>>> {
		return api.get(`/odata/v1/CrossCompanyMirrorConfigs${query}`);
	},

	getCrossCompanyMirrorConfig(id: number): Promise<FetchResponse<CrossCompanyMirrorConfig>> {
		return api.get(`/odata/v1/CrossCompanyMirrorConfigs/${id}`);
	},

	postCrossCompanyMirrorConfig(
		payload: Partial<CrossCompanyMirrorConfig>,
	): Promise<FetchResponse<CrossCompanyMirrorConfig>> {
		return api.post('/api/v1/CrossCompanyMirrorConfigs', payload);
	},

	updateCrossCompanyMirrorConfig(
		id: number,
		payload: Partial<CrossCompanyMirrorConfig>,
	): Promise<FetchResponse<CrossCompanyMirrorConfig>> {
		return api.put(`/api/v1/CrossCompanyMirrorConfigs/${id}`, payload);
	},

	deleteCrossCompanyMirrorConfig(id: number): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/CrossCompanyMirrorConfigs/${id}`);
	},

	getTargetCompanies(): Promise<FetchResponse<string[]>> {
		return api.get('/api/v1/crosscompanymirrorconfigs/target-companies');
	},

	getCrossCompanyWorkers(
		targetCompany: string,
		query: string,
	): Promise<FetchResponse<CrossCompanyWorker[]>> {
		return api.get(
			`/api/v1/crosscompanymirrorconfigs/catalog/workers${query}&targetCompany=${targetCompany}`,
		);
	},

	getCrossCompanyJobOrders(
		targetCompany: string,
		query: string,
	): Promise<FetchResponse<CrossCompanyJobOrder[]>> {
		return api.get(
			`/api/v1/crosscompanymirrorconfigs/catalog/joborders${query}&targetCompany=${targetCompany}`,
		);
	},

	getCrossCompanyJobActivities(
		targetCompany: string,
		query: string,
	): Promise<FetchResponse<CrossCompanyJobActivity[]>> {
		return api.get(
			`/api/v1/crosscompanymirrorconfigs/catalog/jobactivities${query}&targetCompany=${targetCompany}`,
		);
	},
};

export default crossCompanyMirrorConfigEndpoints;
